import React, { useCallback, useId, useRef, useState } from "react";

/**
 * A dependency-free single-series area/line chart for the admin dashboards.
 * Renders as a responsive inline SVG (scales to its container via viewBox),
 * following the house dataviz look: a faint baseline grid, a soft area fill,
 * a crisp line, and an emphasized endpoint dot for "where we are now".
 *
 * Hovering reads out the point under the pointer. A trend line answers "is it
 * going up"; the number behind each week is a separate question, and until it
 * was on hover the only way to get it was to leave the page. The readout is
 * HTML rather than SVG text because the chart stretches (preserveAspectRatio
 * is none) and stretched text is the one thing you cannot read at a glance.
 *
 * Purely presentational — feed it a numeric series and optional x labels.
 */
export type MiniAreaChartProps = {
  /** The data points, left → right. */
  data: number[];
  /** Optional labels under the x-axis, same length as data (a few are shown). */
  labels?: string[];
  /** Accent color for line/fill/dot. Defaults to indigo-500. */
  color?: string;
  /** Fixed max for the y-axis; defaults to the series max (min 1). */
  max?: number;
  /** Format a value for the endpoint tooltip label. */
  formatValue?: (v: number) => string;
  /** Height in px of the drawing area. */
  height?: number;
  className?: string;
};

export default function MiniAreaChart({
  data,
  labels,
  color = "#6366f1",
  max,
  formatValue,
  height = 160,
  className = "",
}: MiniAreaChartProps) {
  const uid = useId().replace(/[:]/g, "");
  const W = 640; // viewBox width (arbitrary units — scales to container)
  const H = 200; // viewBox height
  const padX = 8;
  const padTop = 16;
  const padBottom = 22;

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<number | null>(null);

  const n = data.length;

  /**
   * Which point the pointer is nearest.
   *
   * Measured against the wrapper's own width, not the SVG's unit space: the
   * two differ by exactly the stretch factor, and converting through it is one
   * more place to be wrong.
   */
  const pick = useCallback(
    (clientX: number) => {
      const el = wrapRef.current;
      if (!el || n === 0) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0) return;
      const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      setHover(Math.round(frac * (n - 1)));
    },
    [n]
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (n === 0) return;
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const step = e.key === "ArrowRight" ? 1 : -1;
      setHover((h) => Math.min(n - 1, Math.max(0, (h === null ? n - 1 : h) + step)));
    } else if (e.key === "Escape") {
      setHover(null);
    }
  };

  if (n === 0) {
    return (
      <div className={`flex items-center justify-center text-sm text-slate-400 ${className}`} style={{ height }}>
        —
      </div>
    );
  }

  const hi = Math.max(max ?? 0, ...data, 1);
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;
  const stepX = n > 1 ? innerW / (n - 1) : 0;
  const x = (i: number) => padX + (n > 1 ? i * stepX : innerW / 2);
  const y = (v: number) => padTop + innerH - (Math.max(0, v) / hi) * innerH;

  const linePts = data.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const areaPts = `${padX},${padTop + innerH} ${linePts} ${padX + (n > 1 ? innerW : innerW)},${padTop + innerH}`;

  const lastX = x(n - 1);
  const lastY = y(data[n - 1]);

  // Show at most ~5 evenly spaced x labels to avoid crowding.
  const shownLabelIdx: number[] = [];
  if (labels && labels.length === n) {
    const want = Math.min(5, n);
    for (let k = 0; k < want; k++) {
      const idx = Math.round((k * (n - 1)) / Math.max(1, want - 1));
      if (!shownLabelIdx.includes(idx)) shownLabelIdx.push(idx);
    }
  }

  // Three faint horizontal gridlines (25/50/75% of the range).
  const gridYs = [0.25, 0.5, 0.75].map((f) => padTop + innerH - f * innerH);

  const active = hover !== null && hover >= 0 && hover < n ? hover : null;
  /** Left edge as a percentage of the wrapper, so the readout follows the stretch. */
  const activePct = active === null ? 0 : n > 1 ? (active / (n - 1)) * 100 : 50;
  const activeValue = active === null ? null : data[active];
  const activeLabel = active !== null && labels && labels.length === n ? labels[active] : null;

  return (
    <div
      ref={wrapRef}
      className="relative"
      style={{ height }}
      tabIndex={0}
      role="application"
      aria-label="Grafic — folosește tastele stânga/dreapta pentru a citi valorile"
      onPointerMove={(e) => pick(e.clientX)}
      onPointerDown={(e) => pick(e.clientX)}
      onPointerLeave={() => setHover(null)}
      onBlur={() => setHover(null)}
      onKeyDown={onKeyDown}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        className={className}
        style={{ width: "100%", height, display: "block" }}
      >
        <defs>
          <linearGradient id={`area-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Baseline + faint gridlines */}
        {gridYs.map((gy, i) => (
          <line key={i} x1={padX} y1={gy} x2={W - padX} y2={gy} stroke="currentColor" className="text-slate-100" strokeWidth={1} />
        ))}
        <line x1={padX} y1={padTop + innerH} x2={W - padX} y2={padTop + innerH} stroke="currentColor" className="text-slate-200" strokeWidth={1} />

        {/* Area fill */}
        <polygon points={areaPts} fill={`url(#area-${uid})`} />

        {/* Line */}
        <polyline
          points={linePts}
          fill="none"
          stroke={color}
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* Hovered point: a guide down to the axis, and the point itself */}
        {active !== null && (
          <>
            <line
              x1={x(active)}
              y1={padTop}
              x2={x(active)}
              y2={padTop + innerH}
              stroke={color}
              strokeOpacity={0.35}
              strokeWidth={1}
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
            <circle cx={x(active)} cy={y(data[active])} r={9} fill="#fff" />
            <circle cx={x(active)} cy={y(data[active])} r={5} fill={color} />
          </>
        )}

        {/* Emphasized endpoint — dropped while reading another point, so there
            is only ever one "you are here" on screen. */}
        {active === null && (
          <>
            <circle cx={lastX} cy={lastY} r={4.5} fill={color} />
            <circle cx={lastX} cy={lastY} r={8} fill={color} fillOpacity={0.15} />
          </>
        )}

        {/* X labels */}
        {labels && labels.length === n &&
          shownLabelIdx.map((i) => (
            <text
              key={i}
              x={Math.min(Math.max(x(i), padX + 12), W - padX - 12)}
              y={H - 6}
              textAnchor="middle"
              className="fill-slate-400"
              style={{ fontSize: 11 }}
            >
              {labels[i]}
            </text>
          ))}

        {/* Endpoint value, when nothing else is being read */}
        {formatValue && active === null && (
          <text
            x={Math.min(lastX, W - padX - 4)}
            y={Math.max(lastY - 12, 14)}
            textAnchor="end"
            className="fill-slate-600"
            style={{ fontSize: 12, fontWeight: 700 }}
          >
            {formatValue(data[n - 1])}
          </text>
        )}
      </svg>

      {/* The readout. Clamped inside the wrapper so the first and last points
          do not push it off the card. */}
      {active !== null && activeValue !== undefined && activeValue !== null && (
        <div
          className="absolute top-0 z-10 pointer-events-none"
          style={{
            left: `${activePct}%`,
            transform: `translateX(${activePct < 12 ? "0%" : activePct > 88 ? "-100%" : "-50%"})`,
          }}
        >
          <div className="px-2.5 py-1.5 text-xs font-semibold text-white rounded-lg shadow-lg bg-slate-800/95 whitespace-nowrap">
            {activeLabel && <span className="block font-medium text-slate-300">{activeLabel}</span>}
            <span className="tabular-nums">
              {formatValue ? formatValue(activeValue) : activeValue}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
